// src/pages/Marketing/Marketing.jsx
import React, { useState, useEffect, useMemo } from 'react';

const Marketing = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [activeSection, setActiveSection] = useState('brand-overview');

  const tableOfContents = useMemo(() => [
    { id: 'brand-overview', title: 'Brand Overview', level: 1 },
    { id: 'main-logo', title: 'Main Logo', level: 2 },
    { id: 'brand-icon', title: 'Brand Icon', level: 2 },
    { id: 'tagline', title: 'Brand Tagline', level: 2 },
    { id: 'logo-variations', title: 'Logo Variations', level: 1 },
    { id: 'primary-logo', title: 'Primary Logo', level: 2 },
    { id: 'secondary-logo', title: 'Secondary Logo', level: 2 },
    { id: 'submark-logo', title: 'Submark Logo', level: 2 },
    { id: 'color-palette', title: 'Color Palette', level: 1 },
    { id: 'core-colors', title: 'Core Colors', level: 2 },
    { id: 'supplementary-colors', title: 'Supplementary Colors', level: 2 },
    { id: 'color-usage', title: 'Color Usage Guidelines', level: 2 },
    { id: 'typography', title: 'Typography', level: 1 },
    { id: 'title-font', title: 'Title Font - Migra', level: 2 },
    { id: 'secondary-font', title: 'Secondary Font - DM Sans', level: 2 },
    { id: 'font-hierarchy', title: 'Font Hierarchy', level: 2 },
    { id: 'brand-applications', title: 'Brand Applications', level: 1 },
    { id: 'logo-backgrounds', title: 'Logo on Different Backgrounds', level: 2 },
    { id: 'photography-style', title: 'Photography Style', level: 2 },
    { id: 'visual-elements', title: 'Visual Elements', level: 2 },
    { id: 'brand-voice', title: 'Brand Voice & Messaging', level: 1 },
    { id: 'tone-voice', title: 'Tone of Voice', level: 2 },
    { id: 'key-messages', title: 'Key Messages', level: 2 },
    { id: 'brand-personality', title: 'Brand Personality', level: 2 },
    { id: 'usage-guidelines', title: 'Usage Guidelines', level: 1 },
    { id: 'logo-dos-donts', title: 'Logo Do\'s and Don\'ts', level: 2 },
    { id: 'spacing-guidelines', title: 'Spacing Guidelines', level: 2 },
    { id: 'minimum-sizes', title: 'Minimum Sizes', level: 2 }
  ], []);

  const coreColors = [
    { name: 'Golden Yellow', hex: '#EEC04F', description: 'Primary accent, warmth, optimism' },
    { name: 'Deep Navy', hex: '#6C5DC0', description: 'Trust, stability, professionalism' },
    { name: 'Soft Coral', hex: '#FFD3C2', description: 'Friendly, approachable, modern' },
    { name: 'Vibrant Pink', hex: '#E73C5B', description: 'Energy, innovation, bold moves' }
  ];

  const supplementaryColors = [
    { name: 'Bright Lime', hex: '#EECE4F', description: 'Growth, freshness, prosperity' },
    { name: 'Sage Green', hex: '#65C5F', description: 'Balance, harmony, natural' },
    { name: 'Soft Lavender', hex: '#FFD3C2', description: 'Calm, premium, sophisticated' },
    { name: 'Warm Orange', hex: '#F0CDEF', description: 'Enthusiasm, creativity, warmth' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const currentSection = sections.find(section => {
        if (section) {
          const rect = section.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const styles = {
    container: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      position: 'relative',
      minHeight: '100vh'
    },
    sidebar: {
      width: sidebarOpen ? '350px' : '60px',
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
      position: 'fixed',
      height: '100vh',
      overflowY: 'auto',
      transition: 'width 0.3s ease',
      zIndex: 1000,
      top: '60px'
    },
    sidebarToggle: {
      position: 'absolute',
      top: '20px',
      right: '-15px',
      background: '#667eea',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      zIndex: 1001
    },
    sidebarHeader: {
      padding: '20px',
      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      display: sidebarOpen ? 'block' : 'none'
    },
    sidebarTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#333',
      margin: '0 0 10px 0'
    },
    pdfLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#667eea',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: '500',
      padding: '8px 12px',
      border: '1px solid #667eea',
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    tocList: {
      listStyle: 'none',
      padding: '0',
      margin: '0'
    },
    tocItem: {
      margin: '0'
    },
    tocButton: {
      width: '100%',
      background: 'none',
      border: 'none',
      textAlign: 'left',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontSize: '13px',
      color: '#555'
    },
    tocButtonLevel1: {
      padding: '12px 20px',
      fontWeight: '600',
      color: '#333',
      borderLeft: '3px solid transparent'
    },
    tocButtonLevel2: {
      padding: '8px 20px 8px 35px',
      fontWeight: '500',
      borderLeft: '3px solid transparent'
    },
    tocButtonActive: {
      background: 'rgba(102, 126, 234, 0.1)',
      borderLeftColor: '#667eea',
      color: '#667eea'
    },
    content: {
      marginLeft: sidebarOpen ? '350px' : '60px',
      padding: '40px',
      background: 'white',
      minHeight: '100vh',
      transition: 'margin-left 0.3s ease',
      maxWidth: '1200px',
      lineHeight: '1.6',
      paddingTop: '80px'
    },
    section: {
      marginBottom: '60px',
      scrollMarginTop: '80px'
    },
    h1: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#333',
      marginBottom: '30px',
      borderBottom: '3px solid #667eea',
      paddingBottom: '15px'
    },
    h2: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#444',
      marginTop: '40px',
      marginBottom: '20px'
    },
    h3: {
      fontSize: '22px',
      fontWeight: '600',
      color: '#555',
      marginTop: '30px',
      marginBottom: '15px'
    },
    h4: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#666',
      marginTop: '25px',
      marginBottom: '12px'
    },
    paragraph: {
      fontSize: '16px',
      lineHeight: '1.7',
      color: '#555',
      marginBottom: '16px'
    },
    bulletList: {
      paddingLeft: '20px',
      marginBottom: '16px'
    },
    bulletItem: {
      fontSize: '16px',
      color: '#555',
      marginBottom: '8px',
      lineHeight: '1.6'
    },
    highlightBox: {
      background: 'linear-gradient(135deg, #EEC04F, #E73C5B)',
      color: 'white',
      padding: '40px',
      borderRadius: '20px',
      marginBottom: '40px',
      boxShadow: '0 10px 30px rgba(238, 192, 79, 0.3)',
      textAlign: 'center'
    },
    brandShowcase: {
      background: 'linear-gradient(135deg, #20B2AA 0%, #008B8B 100%)',
      color: 'white',
      padding: '60px 40px',
      borderRadius: '20px',
      marginBottom: '40px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    },
    logoDisplay: {
      fontSize: '72px',
      fontWeight: '700',
      marginBottom: '20px',
      textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
    },
    taglineDisplay: {
      fontSize: '24px',
      fontWeight: '300',
      letterSpacing: '2px',
      opacity: '0.9'
    },
    iconDisplay: {
      fontSize: '120px',
      marginBottom: '30px'
    },
    colorGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    colorCard: {
      borderRadius: '16px',
      padding: '0',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease'
    },
    colorSwatch: {
      height: '120px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '18px',
      fontWeight: '600',
      textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
    },
    colorInfo: {
      padding: '20px',
      background: 'white'
    },
    colorName: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '8px'
    },
    colorHex: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#666',
      fontFamily: 'monospace',
      marginBottom: '8px'
    },
    colorDescription: {
      fontSize: '14px',
      color: '#777',
      lineHeight: '1.4'
    },
    typographyShowcase: {
      background: '#f8fafc',
      padding: '40px',
      borderRadius: '16px',
      marginBottom: '30px'
    },
    migraFont: {
      fontFamily: '"Migra", serif',
      fontSize: '48px',
      fontWeight: '700',
      marginBottom: '20px',
      color: '#333'
    },
    dmSansFont: {
      fontFamily: '"DM Sans", sans-serif',
      fontSize: '18px',
      lineHeight: '1.6',
      color: '#555'
    },
    fontSample: {
      marginBottom: '30px',
      padding: '30px',
      background: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb'
    },
    applicationGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '25px',
      marginBottom: '30px'
    },
    applicationCard: {
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.3s ease'
    },
    brandCard1: {
      background: 'linear-gradient(135deg, #20B2AA 0%, #6C5DC0 100%)',
      color: 'white',
      padding: '40px 30px',
      textAlign: 'center'
    },
    brandCard2: {
      background: 'linear-gradient(135deg, #2F4F2F 0%, #228B22 100%)',
      color: '#EEC04F',
      padding: '40px 30px',
      textAlign: 'center'
    },
    brandCard3: {
      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
      color: '#E73C5B',
      padding: '40px 30px',
      textAlign: 'center'
    },
    guidelineBox: {
      background: '#f0f9ff',
      border: '1px solid #0ea5e9',
      borderRadius: '12px',
      padding: '25px',
      marginBottom: '25px'
    },
    warningBox: {
      background: '#fef3c7',
      border: '1px solid #f59e0b',
      borderRadius: '12px',
      padding: '25px',
      marginBottom: '25px'
    },
    doCard: {
      background: '#f0fdf4',
      border: '1px solid #22c55e',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '15px'
    },
    dontCard: {
      background: '#fef2f2',
      border: '1px solid #ef4444',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '15px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <button 
          style={styles.sidebarToggle}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? '‹' : '›'}
        </button>
        
        {sidebarOpen && (
          <>
            <div style={styles.sidebarHeader}>
              <h2 style={styles.sidebarTitle}>Brand Guide</h2>
              <a 
                href="/gushome-brand-guide.pdf" 
                style={styles.pdfLink} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                📄 Download Brand Guide
              </a>
            </div>
            
            <ul style={styles.tocList}>
              {tableOfContents.map((item) => (
                <li key={item.id} style={styles.tocItem}>
                  <button
                    style={{
                      ...styles.tocButton,
                      ...(item.level === 1 ? styles.tocButtonLevel1 : styles.tocButtonLevel2),
                      ...(activeSection === item.id ? styles.tocButtonActive : {})
                    }}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        {/* Brand Overview */}
        <section id="brand-overview" style={styles.section}>
          <h1 style={styles.h1}>GusHome Brand Guide</h1>
          
          <div style={styles.brandShowcase}>
            <div style={styles.logoDisplay}>
              <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
              <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
            </div>
            <div style={styles.taglineDisplay}>
              TURNING RENTING INTO EQUITY
            </div>
            <div style={styles.iconDisplay}>🏘️</div>
          </div>

          <div id="main-logo" style={styles.section}>
            <h2 style={styles.h2}>Main Logo</h2>
            <p style={styles.paragraph}>
              The GusHome logo combines modern typography with a friendly, approachable feel. The wordmark 
              uses our custom Migra typeface, emphasizing innovation and trust in the real estate technology space.
            </p>
            
            <div style={styles.highlightBox}>
              <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '15px'}}>
                <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
                <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
              </div>
              <p style={{fontSize: '18px', margin: '0', opacity: '0.9'}}>Primary logo configuration</p>
            </div>
          </div>

          <div id="brand-icon" style={styles.section}>
            <h2 style={styles.h2}>Brand Icon</h2>
            <p style={styles.paragraph}>
              Our brand icon represents the diverse, vibrant communities that GusHome serves. The colorful 
              houses with trees symbolize growth, prosperity, and the transformation of renting into equity building.
            </p>
            
            <div style={{...styles.highlightBox, background: 'linear-gradient(135deg, #20B2AA 0%, #008B8B 100%)'}}>
              <div style={{fontSize: '80px', marginBottom: '20px'}}>🏘️</div>
              <p style={{fontSize: '16px', margin: '0', opacity: '0.9'}}>
                Colorful houses with trees representing community and growth
              </p>
            </div>
          </div>

          <div id="tagline" style={styles.section}>
            <h2 style={styles.h2}>Brand Tagline</h2>
            <div style={styles.guidelineBox}>
              <h3 style={{...styles.h3, marginTop: '0', color: '#0ea5e9'}}>
                "TURNING RENTING INTO EQUITY"
              </h3>
              <p style={styles.paragraph}>
                Our tagline captures the core value proposition of GusHome - transforming the traditional 
                rental experience into an equity-building opportunity. It should always appear in uppercase 
                letters with appropriate spacing and contrast.
              </p>
            </div>
          </div>
        </section>

        {/* Logo Variations */}
        <section id="logo-variations" style={styles.section}>
          <h1 style={styles.h1}>Logo Variations</h1>

          <div id="primary-logo" style={styles.section}>
            <h2 style={styles.h2}>Primary Logo</h2>
            <div style={styles.applicationGrid}>
              <div style={styles.brandCard1}>
                <div style={{fontSize: '32px', fontWeight: '700', marginBottom: '15px'}}>
                  <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
                  <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
                </div>
                <p style={{fontSize: '14px', margin: '0', opacity: '0.8'}}>
                  TURNING RENTING INTO EQUITY
                </p>
              </div>
            </div>
          </div>

          <div id="secondary-logo" style={styles.section}>
            <h2 style={styles.h2}>Secondary Logo</h2>
            <div style={styles.applicationGrid}>
              <div style={styles.brandCard2}>
                <div style={{fontSize: '36px', fontWeight: '700', marginBottom: '15px'}}>
                  <span style={{fontFamily: '"Migra", serif'}}>GusHome</span>
                </div>
                <p style={{fontSize: '14px', margin: '0', opacity: '0.8'}}>
                  TURNING RENTING INTO EQUITY
                </p>
              </div>
            </div>
          </div>

          <div id="submark-logo" style={styles.section}>
            <h2 style={styles.h2}>Submark Logo</h2>
            <div style={styles.applicationGrid}>
              <div style={styles.brandCard3}>
                <div style={{fontSize: '48px', fontWeight: '700', marginBottom: '15px'}}>
                  g.
                </div>
                <p style={{fontSize: '12px', margin: '0', opacity: '0.8'}}>
                  Simplified mark for small applications
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section id="color-palette" style={styles.section}>
          <h1 style={styles.h1}>Color Palette</h1>

          <div id="core-colors" style={styles.section}>
            <h2 style={styles.h2}>Core Colors</h2>
            <p style={styles.paragraph}>
              Our core color palette reflects the vibrant, optimistic nature of GusHome while maintaining 
              professionalism and trust. These colors should be used as the primary palette across all brand applications.
            </p>
            
            <div style={styles.colorGrid}>
              {coreColors.map((color, index) => (
                <div key={index} style={styles.colorCard}>
                  <div style={{...styles.colorSwatch, backgroundColor: color.hex}}>
                    {color.hex}
                  </div>
                  <div style={styles.colorInfo}>
                    <div style={styles.colorName}>{color.name}</div>
                    <div style={styles.colorHex}>{color.hex}</div>
                    <div style={styles.colorDescription}>{color.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="supplementary-colors" style={styles.section}>
            <h2 style={styles.h2}>Supplementary Colors</h2>
            <p style={styles.paragraph}>
              These additional colors provide flexibility for various applications while maintaining brand consistency.
            </p>
            
            <div style={styles.colorGrid}>
              {supplementaryColors.map((color, index) => (
                <div key={index} style={styles.colorCard}>
                  <div style={{...styles.colorSwatch, backgroundColor: color.hex}}>
                    {color.hex}
                  </div>
                  <div style={styles.colorInfo}>
                    <div style={styles.colorName}>{color.name}</div>
                    <div style={styles.colorHex}>{color.hex}</div>
                    <div style={styles.colorDescription}>{color.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div id="color-usage" style={styles.section}>
            <h2 style={styles.h2}>Color Usage Guidelines</h2>
            
            <div style={styles.guidelineBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Primary Usage</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Golden Yellow (#EEC04F):</strong> Call-to-action buttons, highlights, positive messaging</li>
                <li style={styles.bulletItem}><strong>Deep Navy (#6C5DC0):</strong> Headers, primary text, professional contexts</li>
                <li style={styles.bulletItem}><strong>Soft Coral (#FFD3C2):</strong> Backgrounds, subtle accents, friendly interactions</li>
                <li style={styles.bulletItem}><strong>Vibrant Pink (#E73C5B):</strong> Error states, urgent notifications, bold statements</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section id="typography" style={styles.section}>
          <h1 style={styles.h1}>Typography</h1>

          <div id="title-font" style={styles.section}>
            <h2 style={styles.h2}>Title Font - Migra</h2>
            
            <div style={styles.fontSample}>
              <div style={styles.migraFont}>Migra - Title Font</div>
              <p style={styles.paragraph}>
                Migra is our primary typeface for headlines, logos, and prominent display text. 
                It provides a modern, sophisticated feel while remaining highly readable.
              </p>
              
              <div style={{fontFamily: '"Migra", serif', marginTop: '30px'}}>
                <div style={{fontSize: '32px', fontWeight: '700', marginBottom: '10px'}}>
                  ABCDEFGHIJKLMNOPQRSTUVWXYZ
                </div>
                <div style={{fontSize: '32px', marginBottom: '10px'}}>
                  abcdefghijklmnopqrstuvwxyz
                </div>
                <div style={{fontSize: '32px', marginBottom: '10px'}}>
                  1234567890 ?!@%$()&*
                </div>
              </div>
            </div>
          </div>

          <div id="secondary-font" style={styles.section}>
            <h2 style={styles.h2}>Secondary Font - DM Sans</h2>
            
            <div style={styles.fontSample}>
              <div style={{...styles.dmSansFont, fontSize: '32px', fontWeight: '600', marginBottom: '20px'}}>
                DM Sans - Secondary Font
              </div>
              <p style={styles.dmSansFont}>
                DM Sans is our secondary typeface for body text, captions, and user interface elements. 
                It offers excellent readability across all platforms and screen sizes.
              </p>
              
              <div style={{fontFamily: '"DM Sans", sans-serif', marginTop: '30px'}}>
                <div style={{fontSize: '24px', fontWeight: '600', marginBottom: '10px'}}>
                  ABCDEFGHIJKLMNOPQRSTUVWXYZ
                </div>
                <div style={{fontSize: '24px', marginBottom: '10px'}}>
                  abcdefghijklmnopqrstuvwxyz
                </div>
                <div style={{fontSize: '24px', marginBottom: '10px'}}>
                  1234567890 ?!@%$()*&
                </div>
              </div>
            </div>
          </div>

          <div id="font-hierarchy" style={styles.section}>
            <h2 style={styles.h2}>Font Hierarchy</h2>
            
            <div style={styles.typographyShowcase}>
              <div style={{fontFamily: '"Migra", serif', fontSize: '48px', fontWeight: '700', marginBottom: '15px', color: '#333'}}>
                H1 - Main Headline
              </div>
              <div style={{fontFamily: '"Migra", serif', fontSize: '36px', fontWeight: '600', marginBottom: '15px', color: '#444'}}>
                H2 - Section Header
              </div>
              <div style={{fontFamily: '"DM Sans", sans-serif', fontSize: '24px', fontWeight: '600', marginBottom: '15px', color: '#555'}}>
                H3 - Subsection Header
              </div>
              <div style={{fontFamily: '"DM Sans", sans-serif', fontSize: '18px', fontWeight: '500', marginBottom: '15px', color: '#666'}}>
                H4 - Minor Header
              </div>
              <div style={{fontFamily: '"DM Sans", sans-serif', fontSize: '16px', lineHeight: '1.6', color: '#555'}}>
                Body text - Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor 
                incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation.
              </div>
            </div>
          </div>
        </section>

        {/* Brand Applications */}
        <section id="brand-applications" style={styles.section}>
          <h1 style={styles.h1}>Brand Applications</h1>

          <div id="logo-backgrounds" style={styles.section}>
            <h2 style={styles.h2}>Logo on Different Backgrounds</h2>
            
            <div style={styles.applicationGrid}>
              <div style={styles.brandCard1}>
                <div style={{fontSize: '28px', fontWeight: '700', marginBottom: '10px'}}>
                  <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
                  <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
                </div>
                <p style={{fontSize: '12px', margin: '0', opacity: '0.8'}}>
                  TURNING RENTING INTO EQUITY
                </p>
              </div>
              
              <div style={styles.brandCard2}>
                <div style={{fontSize: '28px', fontWeight: '700', marginBottom: '10px'}}>
                  <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
                  <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
                </div>
                <p style={{fontSize: '12px', margin: '0', opacity: '0.8'}}>
                  TURNING RENTING INTO EQUITY
                </p>
              </div>
              
              <div style={styles.brandCard3}>
                <div style={{fontSize: '28px', fontWeight: '700', marginBottom: '10px'}}>
                  <span style={{fontFamily: '"Migra", serif'}}>Gus</span>
                  <span style={{fontFamily: '"Migra", serif', fontStyle: 'italic'}}>Home</span>
                </div>
                <p style={{fontSize: '12px', margin: '0', opacity: '0.8'}}>
                  TURNING RENTING INTO EQUITY
                </p>
              </div>
            </div>
          </div>

          <div id="photography-style" style={styles.section}>
            <h2 style={styles.h2}>Photography Style</h2>
            
            <div style={styles.guidelineBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Photography Guidelines</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Bright and optimistic:</strong> Well-lit, vibrant images that convey positivity</li>
                <li style={styles.bulletItem}><strong>Real people:</strong> Authentic moments showing diverse individuals and families</li>
                <li style={styles.bulletItem}><strong>Modern interiors:</strong> Clean, contemporary spaces that feel welcoming</li>
                <li style={styles.bulletItem}><strong>Urban environments:</strong> City living, community spaces, neighborhood scenes</li>
                <li style={styles.bulletItem}><strong>Color harmony:</strong> Images should complement our brand color palette</li>
              </ul>
            </div>
          </div>

          <div id="visual-elements" style={styles.section}>
            <h2 style={styles.h2}>Visual Elements</h2>
            
            <div style={styles.warningBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Key Visual Motifs</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>House Icons:</strong> Colorful, friendly house illustrations representing community</li>
                <li style={styles.bulletItem}><strong>Growth Elements:</strong> Trees, plants, upward arrows symbolizing equity building</li>
                <li style={styles.bulletItem}><strong>Connection Lines:</strong> Subtle lines and paths showing the journey from renting to owning</li>
                <li style={styles.bulletItem}><strong>Rounded Corners:</strong> Soft, approachable design elements throughout</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Brand Voice & Messaging */}
        <section id="brand-voice" style={styles.section}>
          <h1 style={styles.h1}>Brand Voice & Messaging</h1>

          <div id="tone-voice" style={styles.section}>
            <h2 style={styles.h2}>Tone of Voice</h2>
            
            <div style={styles.guidelineBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Brand Personality</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Empowering:</strong> We help people take control of their financial future</li>
                <li style={styles.bulletItem}><strong>Approachable:</strong> Complex real estate concepts made simple and accessible</li>
                <li style={styles.bulletItem}><strong>Innovative:</strong> Pioneering new ways to build wealth through renting</li>
                <li style={styles.bulletItem}><strong>Trustworthy:</strong> Transparent, reliable, and always in our users' best interest</li>
                <li style={styles.bulletItem}><strong>Optimistic:</strong> Positive about the future of renting and wealth building</li>
              </ul>
            </div>
          </div>

          <div id="key-messages" style={styles.section}>
            <h2 style={styles.h2}>Key Messages</h2>
            
            <div style={styles.applicationGrid}>
              <div style={{...styles.fontSample, border: '2px solid #EEC04F'}}>
                <h3 style={{color: '#EEC04F', marginTop: '0'}}>Primary Message</h3>
                <p style={styles.paragraph}>
                  "Turn your rent into equity. GusHome makes it possible to build wealth while you live."
                </p>
              </div>
              
              <div style={{...styles.fontSample, border: '2px solid #6C5DC0'}}>
                <h3 style={{color: '#6C5DC0', marginTop: '0'}}>Value Proposition</h3>
                <p style={styles.paragraph}>
                  "The smart way to rent. Every payment builds your financial future."
                </p>
              </div>
              
              <div style={{...styles.fontSample, border: '2px solid #E73C5B'}}>
                <h3 style={{color: '#E73C5B', marginTop: '0'}}>Call to Action</h3>
                <p style={styles.paragraph}>
                  "Start building equity today. Join the future of renting with GusHome."
                </p>
              </div>
            </div>
          </div>

          <div id="brand-personality" style={styles.section}>
            <h2 style={styles.h2}>Brand Personality</h2>
            
            <div style={styles.typographyShowcase}>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px'}}>
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <div style={{fontSize: '48px', marginBottom: '10px'}}>🚀</div>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Innovative</h4>
                  <p style={{fontSize: '14px', color: '#666'}}>Leading change in real estate</p>
                </div>
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <div style={{fontSize: '48px', marginBottom: '10px'}}>🤝</div>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Trustworthy</h4>
                  <p style={{fontSize: '14px', color: '#666'}}>Reliable and transparent</p>
                </div>
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <div style={{fontSize: '48px', marginBottom: '10px'}}>💪</div>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Empowering</h4>
                  <p style={{fontSize: '14px', color: '#666'}}>Enabling financial growth</p>
                </div>
                <div style={{textAlign: 'center', padding: '20px'}}>
                  <div style={{fontSize: '48px', marginBottom: '10px'}}>☀️</div>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Optimistic</h4>
                  <p style={{fontSize: '14px', color: '#666'}}>Positive about the future</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Usage Guidelines */}
        <section id="usage-guidelines" style={styles.section}>
          <h1 style={styles.h1}>Usage Guidelines</h1>

          <div id="logo-dos-donts" style={styles.section}>
            <h2 style={styles.h2}>Logo Do's and Don'ts</h2>
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px'}}>
              <div>
                <h3 style={{...styles.h3, color: '#22c55e'}}>✅ Do's</h3>
                <div style={styles.doCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Use adequate white space</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>Maintain clear space around the logo equal to the height of the letter 'G'</p>
                </div>
                <div style={styles.doCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Maintain proportions</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>Always use the provided logo files to ensure correct proportions</p>
                </div>
                <div style={styles.doCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Use on appropriate backgrounds</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>Ensure sufficient contrast between logo and background</p>
                </div>
              </div>
              
              <div>
                <h3 style={{...styles.h3, color: '#ef4444'}}>❌ Don'ts</h3>
                <div style={styles.dontCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Don't stretch or distort</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>Never alter the width or height independently</p>
                </div>
                <div style={styles.dontCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Don't change colors</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>Use only approved brand colors for the logo</p>
                </div>
                <div style={styles.dontCard}>
                  <h4 style={{...styles.h4, marginTop: '0'}}>Don't rotate or tilt</h4>
                  <p style={{fontSize: '14px', margin: '0'}}>The logo should always appear horizontal and upright</p>
                </div>
              </div>
            </div>
          </div>

          <div id="spacing-guidelines" style={styles.section}>
            <h2 style={styles.h2}>Spacing Guidelines</h2>
            
            <div style={styles.guidelineBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Clear Space Requirements</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Minimum clear space:</strong> Equal to the height of the letter 'G' in the logo</li>
                <li style={styles.bulletItem}><strong>Text proximity:</strong> Maintain at least 2x clear space when placing near text</li>
                <li style={styles.bulletItem}><strong>Edge placement:</strong> Leave adequate breathing room from page edges</li>
                <li style={styles.bulletItem}><strong>Element separation:</strong> Don't crowd the logo with other design elements</li>
              </ul>
            </div>
          </div>

          <div id="minimum-sizes" style={styles.section}>
            <h2 style={styles.h2}>Minimum Sizes</h2>
            
            <div style={styles.warningBox}>
              <h3 style={{...styles.h3, marginTop: '0'}}>Size Requirements</h3>
              <ul style={styles.bulletList}>
                <li style={styles.bulletItem}><strong>Digital applications:</strong> Minimum 120px wide</li>
                <li style={styles.bulletItem}><strong>Print applications:</strong> Minimum 1 inch (25.4mm) wide</li>
                <li style={styles.bulletItem}><strong>Business cards:</strong> Minimum 0.75 inches (19mm) wide</li>
                <li style={styles.bulletItem}><strong>Favicon/social media:</strong> Use simplified 'g.' submark for small sizes</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Marketing;